/**
 * Lot BA « Nouveautés » (demande de Maxime du 19/10/2026) : le journal des versions (CHANGELOG.md) est écrit écran
 * par écran, chaque ligne commence par Nouveau, Amélioré ou Corrigé. Ce module le lit (serveur : `GET
 * /api/changelog` ; scripts : `npm run release:check`) et prépare ce que l'appli affiche (popup « Quoi de neuf »,
 * notification, page Nouveautés).
 *
 * AUCUN import : le fichier est aussi chargé tel quel par `scripts/release-lib.mjs` (Node lit le TypeScript sans
 * étape de build), qui ne sait pas résoudre les imports `.js` du reste du paquet.
 */

/**
 * Écrans possibles d'une version (liste fermée, dans l'ordre d'affichage). Pour en ajouter un (ex. « Sous-traitants
 * »), l'ajouter ici, puis dans `TAB_OF_SCREEN` de l'appli s'il correspond à un onglet.
 */
export const CHANGELOG_SCREENS = [
  'À faire',
  // Lot BB : onglet Support (conversations Crisp et mails des clients, fournisseurs, mémoire).
  'Support',
  'Projets',
  'Conseiller',
  'Chiffres',
  'Trésorerie',
  'Dépenses',
  'Carte',
  'Agenda',
  'Apporteurs',
  'Retours',
  'Réglages',
  'Page client',
  'Sous-traitants',
  'Mode écran',
  'Notifications',
  // Lot BF : « Mon compte Google » (webhook Google Chat de chaque membre).
  'Mon compte',
  'Sur téléphone',
  'Partout',
] as const;
export type ChangelogScreenName = (typeof CHANGELOG_SCREENS)[number];

export const CHANGE_KINDS = ['new', 'improved', 'fixed'] as const;
export type ChangeKindName = (typeof CHANGE_KINDS)[number];

/** Étiquette écrite dans le journal (`- **Nouveau** · …`) et affichée dans l'appli. */
export const CHANGE_KIND_LABELS: Record<ChangeKindName, string> = {
  new: 'Nouveau',
  improved: 'Amélioré',
  fixed: 'Corrigé',
};

/** Ligne convenue d'une version faite uniquement de technique : elle n'apparaît pas dans l'appli. */
export const NOTHING_VISIBLE_LINE = '_Rien de visible dans le Cockpit._';
export const UPCOMING_TITLE = 'À venir';

export interface ParsedChange {
  kind: ChangeKindName;
  text: string;
}
export interface ParsedScreen {
  screen: string;
  changes: ParsedChange[];
}
export interface ParsedVersion {
  version: string;
  /** AAAA-MM-JJ. */
  date: string;
  /** Vide pour une version technique (`technical`). */
  screens: ParsedScreen[];
  technical: boolean;
}
export interface ParsedUpcoming {
  screens: ParsedScreen[];
  technical: boolean;
  /** Vrai si la section ne contient rien (ni ligne, ni mention technique). */
  empty: boolean;
}
export interface ChangelogProblem {
  /** Numéro de ligne (à partir de 1). */
  line: number;
  message: string;
}
export interface ParsedChangelog {
  /** Versions publiées, dans l'ordre du fichier (la plus récente d'abord). */
  versions: ParsedVersion[];
  upcoming: ParsedUpcoming | null;
  /** Erreurs qui bloquent `release:check` et `release`. */
  problems: ChangelogProblem[];
  /** Mots techniques repérés (avertissement, non bloquant). */
  warnings: ChangelogProblem[];
}

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/;
const VERSION_HEADING = /^## (\d+\.\d+\.\d+) · (\d{2})\/(\d{2})\/(\d{4})\s*$/;
const KIND_OF_LABEL = new Map<string, ChangeKindName>(
  CHANGE_KINDS.map((kind) => [CHANGE_KIND_LABELS[kind], kind]),
);
const CHANGE_LINE = /^- \*\*([^*]+)\*\* · (.+)$/;

/**
 * Mots du développement qui n'ont rien à faire dans le journal (avertissement : « test » ou « migration » peuvent
 * avoir un sens pour l'utilisateur, on laisse trancher la personne qui écrit).
 */
const TECHNICAL_WORDS: readonly [RegExp, string][] = [
  [/\btests?\b/i, 'test'],
  [/\bCI\b/, 'CI'],
  [/\bmigrations?\b/i, 'migration'],
  [/\bdéploie\w*|\bdéploy\w*|\bdéploiements?\b/i, 'déploiement'],
  [/\brefactor\w*|\brefonte du code\b/i, 'refactor'],
  [/\blot [A-Z]{1,3}\b/, 'lot'],
  [/\bdépendances?\b/i, 'dépendance'],
  [/\b[\w-]+\.(?:ts|js|mjs|vue|sql|json|md|yml|yaml|css|html)\b/, 'nom de fichier'],
  [/\/api\//, 'route'],
];

/** Compare deux versions MAJEUR.MINEUR.CORRECTIF (négatif si a < b). Une version illisible vaut 0.0.0. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => (SEMVER.exec(v)?.slice(1) ?? ['0', '0', '0']).map(Number);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < 3; i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function isVersion(value: unknown): value is string {
  return typeof value === 'string' && SEMVER.test(value);
}

interface Block {
  heading: string;
  line: number;
  body: { text: string; line: number }[];
}

function technicalWarnings(text: string, line: number): ChangelogProblem[] {
  return TECHNICAL_WORDS.filter(([re]) => re.test(text)).map(([, word]) => ({
    line,
    message: `mot technique (« ${word} ») : on écrit ce que voit l'utilisateur`,
  }));
}

/** Corps d'une version ou de « À venir » : écrans, lignes typées, mention technique. */
function parseBody(block: Block, problems: ChangelogProblem[], warnings: ChangelogProblem[]) {
  const screens: ParsedScreen[] = [];
  const screenLines = new Map<ParsedScreen, number>();
  let technical = false;
  let current: ParsedScreen | null = null;
  let last: ParsedChange | null = null;
  let lines = 0;
  for (const { text: raw, line } of block.body) {
    const text = raw.trimEnd();
    if (!text.trim()) {
      last = null;
      continue;
    }
    lines += 1;
    if (text.trim() === NOTHING_VISIBLE_LINE) {
      technical = true;
      last = null;
      continue;
    }
    if (text.startsWith('### ')) {
      const screen = text.slice(4).trim();
      if (!(CHANGELOG_SCREENS as readonly string[]).includes(screen))
        problems.push({ line, message: `écran inconnu « ${screen} » (voir la liste en tête du journal)` });
      if (screens.some((s) => s.screen === screen))
        problems.push({ line, message: `écran « ${screen} » en double dans la même version` });
      current = { screen, changes: [] };
      screens.push(current);
      screenLines.set(current, line);
      last = null;
      continue;
    }
    if (text.startsWith('- ')) {
      const match = CHANGE_LINE.exec(text);
      const kind = match ? KIND_OF_LABEL.get(match[1]!.trim()) : undefined;
      if (!match || !kind) {
        problems.push({
          line,
          message:
            'ligne sans type : elle commence par « - **Nouveau** · », « - **Amélioré** · » ou « - **Corrigé** · »',
        });
        last = null;
        continue;
      }
      if (!current) {
        problems.push({ line, message: 'ligne hors écran : écrire d’abord « ### <Écran> »' });
        last = null;
        continue;
      }
      last = { kind, text: match[2]!.trim() };
      current.changes.push(last);
      warnings.push(...technicalWarnings(last.text, line));
      continue;
    }
    // Suite d'une ligne longue (retour à la ligne, en retrait).
    if (last && /^\s+\S/.test(text)) {
      last.text = `${last.text} ${text.trim()}`;
      warnings.push(...technicalWarnings(text, line));
      continue;
    }
    problems.push({ line, message: `ligne inattendue : « ${text.trim().slice(0, 60)} »` });
  }
  for (const screen of screens) {
    if (screen.changes.length === 0)
      problems.push({
        line: screenLines.get(screen) ?? block.line,
        message: `écran « ${screen.screen} » sans aucune ligne`,
      });
  }
  const changes = screens.reduce((n, s) => n + s.changes.length, 0);
  if (technical && changes > 0)
    problems.push({
      line: block.line,
      message: `« ${NOTHING_VISIBLE_LINE} » et des lignes dans la même version : choisir l'un ou l'autre`,
    });
  return { screens: technical ? [] : screens, technical: technical && changes === 0, empty: lines === 0 };
}

/** Lit le journal (CHANGELOG.md). L'en-tête (avant le premier `## `) est libre. */
export function parseChangelog(markdown: string): ParsedChangelog {
  const problems: ChangelogProblem[] = [];
  const warnings: ChangelogProblem[] = [];
  const blocks: Block[] = [];
  markdown.split('\n').forEach((text, index) => {
    if (text.startsWith('## ')) blocks.push({ heading: text.trimEnd(), line: index + 1, body: [] });
    else blocks.at(-1)?.body.push({ text, line: index + 1 });
  });

  const versions: ParsedVersion[] = [];
  let upcoming: ParsedUpcoming | null = null;
  for (const block of blocks) {
    if (block.heading === `## ${UPCOMING_TITLE}`) {
      if (upcoming) problems.push({ line: block.line, message: `section « ${UPCOMING_TITLE} » en double` });
      if (versions.length > 0)
        problems.push({ line: block.line, message: `« ${UPCOMING_TITLE} » doit être avant les versions` });
      upcoming = parseBody(block, problems, warnings);
      continue;
    }
    const match = VERSION_HEADING.exec(block.heading);
    if (!match) {
      problems.push({
        line: block.line,
        message: `titre illisible « ${block.heading} » (attendu : « ## 1.2.0 · 19/10/2026 »)`,
      });
      continue;
    }
    const [, version, day, month, year] = match as unknown as [string, string, string, string, string];
    const date = `${year}-${month}-${day}`;
    if (Number.isNaN(Date.parse(`${date}T12:00:00Z`)) || Number(month) > 12 || Number(day) > 31)
      problems.push({ line: block.line, message: `date illisible dans « ${block.heading} »` });
    const body = parseBody(block, problems, warnings);
    if (body.empty || (!body.technical && body.screens.length === 0))
      problems.push({
        line: block.line,
        message: `version ${version} vide : écrire ses changements, ou « ${NOTHING_VISIBLE_LINE} »`,
      });
    const previous = versions.at(-1);
    if (versions.some((v) => v.version === version))
      problems.push({ line: block.line, message: `version ${version} en double` });
    else if (previous && compareVersions(version, previous.version) >= 0)
      problems.push({
        line: block.line,
        message: `version ${version} après ${previous.version} : la plus récente d'abord`,
      });
    versions.push({ version, date, screens: body.screens, technical: body.technical });
  }
  return { versions, upcoming, problems, warnings };
}

/** Versions qui apparaissent dans l'appli (sans les versions techniques). */
export function visibleVersions<V extends { screens: readonly unknown[] }>(versions: readonly V[]): V[] {
  return versions.filter((v) => v.screens.length > 0);
}

/** Forme commune aux versions lues ici et à celles servies par l'API (contrat `ChangelogVersion`). */
export interface ChangelogVersionLike {
  version: string;
  date: string;
  screens: readonly { screen: string; changes: readonly { kind: ChangeKindName; text: string }[] }[];
}

export interface ChangeCounts {
  new: number;
  improved: number;
  fixed: number;
}

/** Nombre de lignes par type, sur une ou plusieurs versions. */
export function countChanges(versions: readonly ChangelogVersionLike[]): ChangeCounts {
  const counts: ChangeCounts = { new: 0, improved: 0, fixed: 0 };
  for (const version of versions)
    for (const screen of version.screens) for (const change of screen.changes) counts[change.kind] += 1;
  return counts;
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/**
 * « 2 nouveautés, 1 correction » (maquette : Nouveau et Amélioré comptent comme des nouveautés, Corrigé comme des
 * corrections).
 */
export function changeCountsLabel(counts: ChangeCounts): string {
  const news = counts.new + counts.improved;
  const parts: string[] = [];
  if (news > 0) parts.push(plural(news, 'nouveauté', 'nouveautés'));
  if (counts.fixed > 0) parts.push(plural(counts.fixed, 'correction', 'corrections'));
  return parts.join(', ');
}

/** Écrans touchés, dans l'ordre de la liste fermée (les inconnus à la fin). */
export function screensOf(versions: readonly ChangelogVersionLike[]): string[] {
  const seen = new Set<string>();
  for (const version of versions) for (const screen of version.screens) seen.add(screen.screen);
  return sortScreens([...seen]);
}

export function sortScreens(screens: readonly string[]): string[] {
  const rank = (s: string) => {
    const i = (CHANGELOG_SCREENS as readonly string[]).indexOf(s);
    return i < 0 ? CHANGELOG_SCREENS.length : i;
  };
  return [...screens].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b, 'fr'));
}

/** Notification de la cloche : « Cockpit 1.3.0 : 2 nouveautés, 1 correction », écrans touchés en texte. */
export function releaseNotification(version: ChangelogVersionLike): { title: string; body: string } {
  const counts = changeCountsLabel(countChanges([version]));
  return {
    title: counts ? `Cockpit ${version.version} : ${counts}` : `Cockpit ${version.version}`,
    body: screensOf([version]).join(', '),
  };
}

/**
 * Versions du « Quoi de neuf » : les versions visibles plus récentes que la dernière vue, jusqu'à la version de
 * l'appli ouverte (on ne montre pas ce qu'on n'a pas encore). Tout premier passage (rien de vu) : seulement la
 * dernière. Résultat de la plus récente à la plus ancienne.
 */
export function unseenVersions<V extends ChangelogVersionLike>(
  versions: readonly V[],
  lastSeen: string | null | undefined,
  upTo?: string | null,
): V[] {
  const candidates = visibleVersions(versions)
    .filter((v) => !upTo || compareVersions(v.version, upTo) <= 0)
    .sort((a, b) => compareVersions(b.version, a.version));
  if (!lastSeen) return candidates.slice(0, 1);
  return candidates.filter((v) => compareVersions(v.version, lastSeen) > 0);
}

export interface GroupedChange {
  kind: ChangeKindName;
  text: string;
  version: string;
}
export interface GroupedScreen {
  screen: string;
  changes: GroupedChange[];
}

/**
 * Plusieurs versions regroupées et rangées par écran (ordre de la liste fermée), les lignes de la plus récente
 * version d'abord.
 */
export function groupByScreen(versions: readonly ChangelogVersionLike[]): GroupedScreen[] {
  const byScreen = new Map<string, GroupedChange[]>();
  const sorted = [...versions].sort((a, b) => compareVersions(b.version, a.version));
  for (const version of sorted)
    for (const screen of version.screens) {
      const list = byScreen.get(screen.screen) ?? [];
      list.push(...screen.changes.map((c) => ({ kind: c.kind, text: c.text, version: version.version })));
      byScreen.set(screen.screen, list);
    }
  return sortScreens([...byScreen.keys()]).map((screen) => ({ screen, changes: byScreen.get(screen)! }));
}
